import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import VideoCall from '../video-call/VideoCall';
import LogoLoader from '../LogoLoader';
import styled from 'styled-components';
import { useAuth0 } from '@auth0/auth0-react';
import useAuthenticatedAxios from '../../auth/AuthenticatedAxios';

const VideoCallPageContainer = styled.div`
  // height: 100vh;
  width: 100%;
  background: linear-gradient(180deg, #f9fbff 0%, #f5f8fd 100%);
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  color: #192947;
  font-size: 18px;
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100vh;
  color: #192947;
  text-align: center;
  padding: 20px;
`;

const ErrorButton = styled.button`
  margin-top: 20px;
  padding: 12px 24px;
  background: linear-gradient(110deg, #2d5bed, #5b61ea);
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-size: 16px;
  
  &:hover {
    background: #2d5bed;
  }
`;

const VideoCallPage = () => {
  const { expertId, channelName } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth0();
  const [callConfig, setCallConfig] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const authenticatedAxios = useAuthenticatedAxios();

  useEffect(() => {
    const initializeCall = async () => {
      try {
        setIsLoading(true);
        setError(null);

        if (!expertId || !channelName) {
          setError('Missing required parameters: expertId and channelName');
          setIsLoading(false);
          return;
        }

        // Get call configuration from server
        const response = await authenticatedAxios.post(`/api/video-call/join-room`, {
          expertId: expertId,
          channelName: channelName
        });

        if (response.status !== 200) {
          throw new Error('Failed to get call configuration');
        }


        const resData = await response.data;
        const config = {
          appId: process.env.REACT_APP_AGORA_APP_ID,
          channelName: resData.data.channelName,
          token: resData.data.token,
          uid: resData.data.uid,
          expertName: resData.data.expert.name
        };
        setCallConfig(config);
        setIsLoading(false);

      } catch (err) {
        console.error('Error initializing video call:', err);
        setError('Failed to initialize video call');
        setIsLoading(false);
      }
    };

    if (isAuthenticated) {
      initializeCall();
    } else {
      setIsLoading(false);
      setError('Authentication required');
    }
  }, [expertId, channelName, isAuthenticated]);

  const handleCallEnd = (duration) => {
    console.log(`Call ended after ${duration} seconds`);
    // Navigate to the main conversations page
    navigate('/conversations');
  };

  const handleError = (error) => {
    console.error('Video call error:', error);
    setError('Video call encountered an error');
  };

  if (!isAuthenticated) {
    return (
      <ErrorContainer>
        <h2>Authentication Required</h2>
        <p>Please log in to access the video call feature.</p>
        <ErrorButton onClick={() => navigate('/')}>
          Go to Home
        </ErrorButton>
      </ErrorContainer>
    );
  }

  if (isLoading) {
    return (
      <LoadingContainer>
        <LogoLoader size={180} message="Starting your video call" />
      </LoadingContainer>
    );
  }

  if (error) {
    return (
      <ErrorContainer>
        <h2>Error</h2>
        <p>{error}</p>
        <ErrorButton onClick={() => navigate(-1)}>
          Go Back
        </ErrorButton>
      </ErrorContainer>
    );
  }

  if (callConfig) {
    return (
      <VideoCallPageContainer>
        <VideoCall
          appId={callConfig.appId}
          channelName={callConfig.channelName}
          token={callConfig.token}
          uid={callConfig.uid}
          expertName={callConfig.expertName}
          onCallEnd={handleCallEnd}
          onError={handleError}
        />
      </VideoCallPageContainer>
    );
  }

  return (
    <ErrorContainer>
      <h2>Unable to Start Call</h2>
      <p>Please check your configuration and try again.</p>
      <ErrorButton onClick={() => navigate(-1)}>
        Go Back
      </ErrorButton>
    </ErrorContainer>
  );
};

export default VideoCallPage; 